if (!window.customElements.get('saba-gift-card-balance')) {
  class SabaGiftCardBalance extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.tile = this.querySelector('[data-tile]');
      this.errorEl = this.querySelector('[data-error]');
      this.careEl = this.querySelector('[data-care]');
      this.submitBtn = this.form?.querySelector('button[type="submit"]');
      this.submitLabelEl = this.submitBtn?.querySelector('.saba-btn__label');
      this.submitLabelDefault = this.submitLabelEl?.textContent ?? '';
      this.checkingLabel = this.dataset.checkingLabel || '';
      this.numberInput = this.form?.querySelector('[name="gift_card_number"]');
      this.pinInput = this.form?.querySelector('[name="gift_card_pin"]');
      this.apiUrl = this.dataset.apiUrl || '';
      this.moneyFormat = this.dataset.moneyFormat || '';
      // Market enforcement: NZ balance checks must carry the NZ country code so
      // Omneo routes to the NZ Retail API; AU/other markets send none. Set from
      // the section's data-country-code (derived from localization.market.handle).
      this.countryCode = this.dataset.countryCode || '';
      this.errors = {
        emptyFields: this.dataset.errorEmptyFields || '',
        invalidPin: this.dataset.errorInvalidPin || '',
        digitsOnly: this.dataset.errorDigitsOnly || '',
        notConfigured: this.dataset.errorNotConfigured || '',
        notFound: this.dataset.errorNotFound || '',
        wrongMarket: this.dataset.errorWrongMarket || '',
        expired: this.dataset.errorExpired || '',
        redeemed: this.dataset.errorRedeemed || '',
        fetchFailed: this.dataset.errorFetchFailed || '',
      };

      this.boundOnSubmit = this.onSubmit.bind(this);
      this.boundOnInputChange = this.onInputChange.bind(this);

      if (this.form) {
        this.form.addEventListener('submit', this.boundOnSubmit);
      }

      if (this.numberInput) {
        this.numberInput.addEventListener('input', this.boundOnInputChange);
      }
      if (this.pinInput) {
        this.pinInput.addEventListener('input', this.boundOnInputChange);
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundOnSubmit);
      this.numberInput?.removeEventListener('input', this.boundOnInputChange);
      this.pinInput?.removeEventListener('input', this.boundOnInputChange);
    }

    onInputChange(event) {
      // Sanitise as the shopper types: digits only, PIN capped at 4 (WS6). A
      // non-digit keystroke shows an immediate "numbers only" error rather than
      // being silently swallowed.
      const el = event && event.target;
      const hadNonDigit = !!(el && /\D/.test(el.value));
      if (el === this.numberInput) {
        const clean = el.value.replace(/\D/g, '');
        if (clean !== el.value) el.value = clean;
      } else if (el === this.pinInput) {
        const clean = el.value.replace(/\D/g, '').slice(0, 4);
        if (clean !== el.value) el.value = clean;
      }
      // Changing an input after a successful lookup resets the tile so the
      // next check reflects the new number/pin rather than stale data.
      if (this.dataset.state === 'loaded') {
        this.hideTile();
      }
      if (hadNonDigit && this.errors.digitsOnly) {
        this.showError(this.errors.digitsOnly, false);
      } else {
        this.clearError();
      }
    }

    // Map the worker's `reason` code to a specific message (WS7).
    messageForReason(reason) {
      switch (reason) {
        case 'expired': return this.errors.expired || this.errors.notFound;
        case 'already_redeemed': return this.errors.redeemed || this.errors.notFound;
        case 'wrong_market': return this.errors.wrongMarket || this.errors.notFound;
        default: return this.errors.notFound;
      }
    }

    // Scope (doc 663205): every voucher-state error shows the Care link.
    careForReason(_reason) {
      return true;
    }

    async onSubmit(event) {
      event.preventDefault();

      const number = (this.numberInput?.value || '').trim();
      const pin = (this.pinInput?.value || '').trim();

      if (!number || !pin) {
        this.showError(this.errors.emptyFields);
        return;
      }
      if (!/^\d{4}$/.test(pin)) {
        this.showError(this.errors.invalidPin);
        return;
      }
      if (!this.apiUrl) {
        this.showError(this.errors.notConfigured);
        return;
      }

      this.setLoading(true);
      this.clearError();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const res = await fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'balance', number, pin, ...(this.countryCode ? { country_code: this.countryCode } : {}) }),
          signal: controller.signal,
        });
        // 5xx = infrastructure failure → "try again"; 4xx carries a voucher
        // `reason` (not found / expired / redeemed) from the worker (WS7).
        if (res.status >= 500) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));

        const hasBalance = data && (data.balance != null || (data.value_remaining != null && data.value_remaining !== ''));
        if (res.ok && data.status !== 'unknown' && hasBalance && !data.reason) {
          this.renderTile(data, number);
          this.dataset.state = 'loaded';
        } else {
          this.showError(this.messageForReason(data.reason), this.careForReason(data.reason));
        }
      } catch (_err) {
        this.showError(this.errors.fetchFailed);
      } finally {
        clearTimeout(timeoutId);
        this.setLoading(false);
      }
    }

    renderTile(data, submittedNumber) {
      const setField = (key, value) => {
        const node = this.tile?.querySelector(`[data-field="${key}"]`);
        if (node) node.textContent = value == null || value === '' ? '—' : value;
      };

      const cardNumber = this.formatGroupedNumber(data.id || submittedNumber);
      const expiry = this.formatDate(data.expires_at);
      const initial = this.toNumber(data.value_initial);
      const remaining = this.toNumber(data.value_remaining != null ? data.value_remaining : data.balance);
      const used = initial != null && remaining != null ? initial - remaining : null;

      setField('card-number', cardNumber);
      setField('expiry-date', expiry);
      setField('original-amount', this.formatCurrency(initial));
      setField('used-amount', this.formatCurrency(used));
      setField('current-balance', this.formatCurrency(remaining));

      if (this.tile) this.tile.hidden = false;
    }

    toNumber(value) {
      if (value == null || value === '') return null;
      const num = typeof value === 'number' ? value : parseFloat(value);
      return Number.isNaN(num) ? null : num;
    }

    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    hideTile() {
      if (this.tile) this.tile.hidden = true;
      if (this.dataset.state === 'loaded') this.dataset.state = 'input';
    }

    showError(message, withCare) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      // Show the Customer Care link for voucher-state errors only (WS7).
      if (this.careEl) this.careEl.hidden = !withCare;
      this.dataset.state = 'error';
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
      if (this.careEl) this.careEl.hidden = true;
      if (this.dataset.state === 'error') this.dataset.state = 'input';
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.dataset.state = 'loading';
        if (this.submitBtn) this.submitBtn.disabled = true;
        if (this.submitLabelEl) this.submitLabelEl.textContent = this.checkingLabel;
      } else {
        if (this.dataset.state === 'loading') this.dataset.state = 'input';
        if (this.submitBtn) this.submitBtn.disabled = false;
        if (this.submitLabelEl) this.submitLabelEl.textContent = this.submitLabelDefault;
      }
    }

    formatCurrency(value) {
      if (value == null || value === '') return '—';
      const num = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isNaN(num)) return '—';
      if (!this.moneyFormat) return num.toFixed(2);
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
        switch (token) {
          case 'amount': return num.toFixed(2);
          case 'amount_no_decimals': return Math.round(num).toString();
          case 'amount_with_comma_separator': return num.toFixed(2).replace('.', ',');
          case 'amount_no_decimals_with_comma_separator': return Math.round(num).toString();
          default: return num.toFixed(2);
        }
      });
    }

    formatGroupedNumber(value) {
      const digits = String(value == null ? '' : value).replace(/\D/g, '');
      if (!digits) return '—';
      return digits.match(/.{1,4}/g).join(' ');
    }
  }

  customElements.define('saba-gift-card-balance', SabaGiftCardBalance);
}
